// The quality read of every material the chat made, through the sanctioned
// port seam (`evaluateMaterials`: ONE pure server read for all of them, no LLM,
// no command, so it never counts against the command rate limit).
//
// The panel's home AND the material's two lenses project from this ONE read,
// so they can never disagree. It re-reads whenever `worldKey` changes: the host
// passes the library value, which is re-read after every turn (`Signal.
// WorldChanged` bumps the shell's re-read). That is the live path for a turn
// that adds citations (UX review G2).
//
// A material is re-evaluated only when its version moved: a read is cached by
// material id + version (`qualityVersionOf`: the library's `lastEditedAt` plus
// its `groundingRevisedAt`, since a citation or a claim-support verdict changes
// the grounding without editing the material), and a material without a
// version is never cached. A read in flight keeps the previous value; a
// failed read leaves the material unread (the views say "not checked", never a
// guess). Cancellation-guarded: the last key wins.

import { Result } from "@bilda/utils";
import { useEffect, useRef, useState } from "react";
import type { MaterialQuality } from "../../../components/QualitySummary/QualitySummary.js";
import type { MaterialQualityRead } from "../../ports/quality.js";
import type { AppPorts } from "../../ports.js";

// What a material's quality read depends on, from its library row.
export const qualityVersionOf = (m: {
    readonly lastEditedAt?: string;
    readonly groundingRevisedAt?: string;
}): string | undefined =>
    m.lastEditedAt === undefined
        ? undefined
        : `${m.lastEditedAt}|${m.groundingRevisedAt ?? ""}`;

export type VersionedMaterial = {
    readonly id: string;
    readonly version: string | undefined;
};

type Cached = {
    readonly version: string | undefined;
    readonly quality: MaterialQuality;
};

const isFresh = (
    cache: ReadonlyMap<string, Cached>,
    { id, version }: VersionedMaterial,
): boolean => version !== undefined && cache.get(id)?.version === version;

export const useMaterialQualities = (
    evaluateMaterials: AppPorts["evaluateMaterials"],
    materials: readonly VersionedMaterial[],
    worldKey: unknown,
): ReadonlyMap<string, MaterialQuality> => {
    const cache = useRef<ReadonlyMap<string, Cached>>(new Map());
    const [qualities, setQualities] = useState<
        ReadonlyMap<string, MaterialQuality>
    >(new Map());
    const key = materials
        .map(({ id, version }) => `${id}@${version}`)
        .join("\n");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `materials` (a fresh array each render); `worldKey` IS the re-read signal, the body needs no value from it.
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const stale = materials.filter((m) => !isFresh(cache.current, m));
            const batch =
                stale.length === 0
                    ? Result.ok<readonly MaterialQualityRead[]>([])
                    : await evaluateMaterials(stale.map(({ id }) => id));
            if (cancelled || Result.isErr(batch)) return;
            const read = new Map(
                batch.value.flatMap(
                    ({ id, read }): [string, MaterialQuality][] =>
                        Result.isOk(read) ? [[id, read.value]] : [],
                ),
            );
            const staleIds = new Set(stale.map(({ id }) => id));
            const next = new Map(
                materials.flatMap(({ id, version }): [string, Cached][] => {
                    const quality = staleIds.has(id)
                        ? read.get(id)
                        : cache.current.get(id)?.quality;
                    return quality === undefined
                        ? []
                        : [[id, { version, quality }]];
                }),
            );
            cache.current = next;
            setQualities(
                new Map([...next].map(([id, { quality }]) => [id, quality])),
            );
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [evaluateMaterials, key, worldKey]);
    return qualities;
};
